"use client";

import {
  AdsContextProps,
  AdsProvider,
  defaultAdsContext,
} from "@hopper-b2b/ads-api";
import { ExperimentsProvider } from "@hopper-b2b/experiments";
import {
  FlashSaleProvider,
  IFlashSaleContextProps,
  defaultFlashSaleContext,
} from "@hopper-b2b/flashsale";
import {
  IAxiosInterceptors,
  UnauthorizationProvider,
  useAuthSessionContext,
} from "@hopper-b2b/hopper-auth";
import {
  HopperThemingProvider,
  branding,
  tenantContext as defaultTenantContext,
} from "@hopper-b2b/hopper-theming";
import {
  apiConfig,
  defaultLanguage,
  getCurrentLanguage,
} from "@hopper-b2b/hopper-utils";
import I18nProvider, {
  Translation,
  TranslationLanguage,
  en_hopperTranslations,
  es_hopperTranslations,
  fr_hopperTranslations,
  getLang,
  setLang,
  DEFAULT_LANG as I18N_DEFAULT_LANG,
} from "@hopper-b2b/i18n";
import { ExperimentsType } from "@hopper-b2b/types";
import { UserSettingsProvider } from "@hopper-b2b/user-settings";
import {
  AuthProvider,
  AuthProviderProps,
  FeatureFlagsSessionIdRefresher,
  ISSRContextProps,
  ITenantContextProps,
  SSRContextProvider,
  SessionContextProvider,
  TenantContextProvider,
  defaultSSRContext,
  useAuthProvider,
} from "@hopper-b2b/utilities";
import { setupListeners } from "@reduxjs/toolkit/query";
import { PropsWithChildren, useMemo, useState } from "react";
import { Provider } from "react-redux";
import { ConfigureStoreOptions, createStore } from "./store";

export const DEFAULT_LANG = I18N_DEFAULT_LANG;

export type HopperProvidersProps = IAxiosInterceptors &
  ConfigureStoreOptions & {
    tenantContext?: ITenantContextProps;
    ssrContext?: ISSRContextProps;
    flashSaleContext?: IFlashSaleContextProps;
    adsContext?: AdsContextProps;
    enableHDSTheme?: boolean;
    translationLanguage?: string;
    initialExperiments?: ExperimentsType;
    country?: string;
    loadingFallback?: React.ReactNode;
    /** See ExperimentsProvider's `renderWithoutSession`; off by default. */
    renderWithoutSession?: boolean;
  };

export const HopperProviders = ({
  initState,
  ...rest
}: PropsWithChildren<HopperProvidersProps> & AuthProviderProps) => {
  return (
    <AuthProvider initState={initState}>
      <HopperProvidersInternal {...rest} />
    </AuthProvider>
  );
};

const HopperProvidersInternal = ({
  version,
  recaptchaActionKey,
  tenantContext,
  ssrContext = defaultSSRContext,
  flashSaleContext = defaultFlashSaleContext,
  adsContext = defaultAdsContext,
  enableHDSTheme,
  translationLanguage,
  initialExperiments,
  country,
  children,
  reducers,
  middlewares,
  loadingFallback,
  renderWithoutSession,
}: PropsWithChildren<HopperProvidersProps>) => {
  const locale = getLang();

  const sessionContext = useAuthSessionContext();
  const userInfoContext = useAuthProvider();

  const [defaultLng] = useState<string>(() => {
    const currentLanguage = getCurrentLanguage();
    setLang(
      currentLanguage === defaultLanguage ? DEFAULT_LANG : currentLanguage
    );
    return currentLanguage;
  });

  const tenantTranslation: Translation = useMemo(() => {
    return {
      [TranslationLanguage.en]: en_hopperTranslations,
      [TranslationLanguage.es]: es_hopperTranslations,
      [TranslationLanguage.fr]: fr_hopperTranslations,
    };
  }, []);

  const store = useMemo(
    () => createStore({ reducers, middlewares }),
    [middlewares, reducers]
  );
  setupListeners(store.dispatch);
  return (
    <Provider store={store}>
      <SSRContextProvider ssrContext={ssrContext} locale={locale}>
        <SessionContextProvider sessionContext={sessionContext}>
          <I18nProvider
            defaultLng={defaultLng}
            branding={branding}
            tenantTranslation={tenantTranslation}
            localeParam={translationLanguage}
          >
            <UnauthorizationProvider
              version={version}
              recaptchaActionKey={recaptchaActionKey}
              loadingFallback={loadingFallback}
            >
              <ExperimentsProvider
                apiConfig={apiConfig}
                sessionContext={sessionContext}
                initialExperiments={initialExperiments}
                isServerSideRendering={ssrContext.isServerSideRendering}
                loadingFallback={loadingFallback}
                renderWithoutSession={renderWithoutSession}
              >
                <FlashSaleProvider
                  isLoggedIn={!!sessionContext}
                  flashSaleContext={flashSaleContext}
                  country={country}
                >
                  <AdsProvider adsContext={adsContext}>
                    <UserSettingsProvider userInfoContext={userInfoContext}>
                      <HopperThemingProvider enableHDSTheme={enableHDSTheme}>
                        <TenantContextProvider
                          tenantContext={tenantContext || defaultTenantContext}
                        >
                          <FeatureFlagsSessionIdRefresher>
                            {children}
                          </FeatureFlagsSessionIdRefresher>
                        </TenantContextProvider>
                      </HopperThemingProvider>
                    </UserSettingsProvider>
                  </AdsProvider>
                </FlashSaleProvider>
              </ExperimentsProvider>
            </UnauthorizationProvider>
          </I18nProvider>
        </SessionContextProvider>
      </SSRContextProvider>
    </Provider>
  );
};
